import { FC } from "react"
import { Formik } from "formik"
import { object, string } from "yup"
import { REGEX_HEX_COLOR } from "../../../common/constants"
import { LeadworkflowDetails } from "../../../types"
import ManageLeadworkflowFormContent from "../../../common/ui/config-admin/config-lead-workflow/manage/form-content"
import Tabs from "../../../common/ui/tabs"
import ConfigEditHeader from "../../../common/ui/config-admin/config-edit-header"
import {
  useDeleteLeadworkflowMutation,
  useFetchDefaultLeadworkflowDetailsQuery,
  useFetchLeadworkflowDetailsQuery,
  useUpdateOrCreateLeadworkflowMutation
} from "../../../redux/queries"
import { ConfigEditPageSkeleton } from "../../../common/ui/skeletons"
import { useConfigEditPage } from "../use-config-edit-page"

const TABS = [
  { id: "behaviour", label: "Behaviour" },
  { id: "disclaimers", label: "Disclaimers" },
  { id: "steps", label: "Steps" }
]

const LeadworkflowSchema = object({
  name: string().required().min(1),
  config_json: object({
    unique_application_url: string()
      .transform(v => (v === "" || v == null ? undefined : v))
      .optional()
      .url("Must be a valid URL"),
    primary_color: string().test(
      "hex",
      "Is not a valid hex color",
      value => value == null || value === "" || REGEX_HEX_COLOR.test(value)
    )
  }).required(),
  brand_id: string().required().min(1)
})

const LeadWorkflowEditPage: FC = () => {
  const [updateOrCreate, saveResult] = useUpdateOrCreateLeadworkflowMutation()
  const [deleteLeadworkflow, deleteResult] = useDeleteLeadworkflowMutation()

  const {
    id,
    isCreate,
    formRef,
    setFormField,
    tab,
    setTab,
    brands,
    navigateBack
  } = useConfigEditPage<LeadworkflowDetails>({
    tabs: TABS,
    tab: "lead-workflow",
    label: "lead workflow",
    saveResult,
    deleteResult
  })

  const { data: workflowData, isLoading } = useFetchLeadworkflowDetailsQuery(
    { id: id ?? "" },
    { skip: isCreate || !id }
  )

  const { data: defaultData } = useFetchDefaultLeadworkflowDetailsQuery(
    {},
    { skip: !isCreate }
  )

  const raw = workflowData ?? {
    name: "",
    id: "",
    brand_id: "",
    ...(defaultData || { is_default: "0", config_json: {} })
  }

  const initialValues: LeadworkflowDetails = {
    ...raw,
    config_json: {
      show_txt_msgs_disclaimer: false,
      ...raw.config_json
    }
  }

  const onSubmit = () => {
    if (formRef.current?.values) updateOrCreate(formRef.current.values)
  }

  const onDelete = () => {
    if (workflowData?.id) deleteLeadworkflow(workflowData.id)
  }

  if (!isCreate && isLoading) {
    return <ConfigEditPageSkeleton tabCount={3} />
  }

  const pageTitle = isCreate
    ? "New lead workflow"
    : workflowData?.name ?? "Lead workflow"

  return (
    <div className="min-h-screen bg-gray-50">
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        innerRef={formRef}
        validationSchema={LeadworkflowSchema}
        enableReinitialize
      >
        {({ values, isValid, dirty }) => (
          <>
            <ConfigEditHeader
              allowEditName
              backLabel="Lead workflows"
              namePlaceholder="Workflow name"
              name={values.name}
              pageTitle={pageTitle}
              isCreate={isCreate}
              isValid={isValid}
              dirty={dirty}
              isDefault={values.is_default === "1"}
              brands={brands ?? []}
              brandId={values.brand_id}
              onNameChange={val => setFormField("name", val)}
              onDefaultChange={v => setFormField("is_default", v ? "1" : "0")}
              onBrandChange={val => setFormField("brand_id", val)}
              onDelete={onDelete}
              onCancel={() => navigateBack(values.brand_id)}
              onSubmit={onSubmit}
              onBack={() => navigateBack(values.brand_id)}
            />

            <Tabs
              currentTab={tab}
              tabs={TABS}
              onChange={setTab}
              className="px-6 bg-white"
            />

            <div className="bg-white">
              <div className="max-w-2xl py-6 px-6">
                <ManageLeadworkflowFormContent
                  values={values}
                  tab={tab}
                  setFormField={setFormField}
                />
              </div>
            </div>
          </>
        )}
      </Formik>
    </div>
  )
}

export default LeadWorkflowEditPage
